import React, { useState, useEffect } from 'react';
import { apiKeysAPI } from '../services/api';
import {
  Key,
  Plus,
  Copy,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Activity,
  AlertCircle,
  CheckCircle,
  FileText
} from 'lucide-react';
import { toast } from 'react-toastify';

const ApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [showNewKey, setShowNewKey] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedKeyForLogs, setSelectedKeyForLogs] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsStats, setLogsStats] = useState(null);
  const [logsPagination, setLogsPagination] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    fetchApiKeys();
    fetchUsageStats();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await apiKeysAPI.getAll();
      setApiKeys(response.data.data.apiKeys);
      setSubscription(response.data.data.subscription);
    } catch (error) {
      toast.error('Failed to fetch API keys');
      console.error('Error fetching API keys:', error);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const response = await apiKeysAPI.getUsageStats(7);
      setUsageStats(response.data.data);
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (keyData) => {
    try {
      const response = await apiKeysAPI.create(keyData);
      setApiKeys([...apiKeys, response.data.data.apiKey]);
      setShowNewKey(response.data.data.apiKey);
      setShowCreateModal(false);
      toast.success('API key created successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create API key');
    }
  };

  const handleUpdateKey = async (keyId, keyData) => {
    try {
      const response = await apiKeysAPI.update(keyId, keyData);
      setApiKeys(apiKeys.map(key => key.id === keyId ? response.data.data.apiKey : key));
      setShowEditModal(false);
      setEditingKey(null);
      toast.success('API key updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiKeysAPI.delete(keyId);
      setApiKeys(apiKeys.filter(key => key.id !== keyId));
      toast.success('API key deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete API key');
    }
  };

  const handleToggleKey = async (keyId) => {
    try {
      const response = await apiKeysAPI.toggle(keyId);
      setApiKeys(apiKeys.map(key => key.id === keyId ? response.data.data.apiKey : key));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle API key');
    }
  };

  const handleViewLogs = async (apiKey, page = 1) => {
    setSelectedKeyForLogs(apiKey);
    setShowLogsModal(true);
    setLogsLoading(true);

    try {
      const response = await apiKeysAPI.getLogs(apiKey.id, { page, limit: 50 });
      setLogs(response.data.data.logs);
      setLogsStats(response.data.data.statistics);
      setLogsPagination(response.data.data.pagination);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLogsPageChange = (page) => {
    if (selectedKeyForLogs) {
      handleViewLogs(selectedKeyForLogs, page);
    }
  };

  const copyToClipboard = (text) => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success('Copied to clipboard');
      }).catch(() => {
        fallbackCopyToClipboard(text);
      });
    } else {
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      // Try to copy using execCommand
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Copied to clipboard');
      } else {
        toast.error('Failed to copy to clipboard');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      toast.error('Copy not supported in this browser');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Key className="w-8 h-8 mr-3 text-blue-600" />
              API Keys
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your API keys for accessing TCG data
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={subscription && !subscription.canCreateMore}
            className={`btn btn-md flex items-center ${
              subscription && !subscription.canCreateMore
                ? 'btn-gray cursor-not-allowed opacity-50'
                : 'btn-primary'
            }`}
            title={
              subscription && !subscription.canCreateMore
                ? `You've reached your limit of ${subscription.maxApiKeys} API keys`
                : 'Create a new API key'
            }
          >
            <Plus className="w-4 h-4 mr-2" />
            Create API Key
          </button>
        </div>
      </div>

      {/* Subscription Status and Usage Bar */}
      {subscription && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Subscription Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Key className="w-5 h-5 mr-2 text-blue-600" />
              Subscription Status
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Current Plan:</span>
                <span className="font-medium capitalize">{subscription.tier || 'Free'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">API Keys:</span>
                <span className="font-medium">{subscription.currentKeys || 0} / {subscription.maxApiKeys || 5}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Daily Limit:</span>
                <span className="font-medium">{(subscription.dailyRequestLimit || 0).toLocaleString()} requests</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Minute Limit:</span>
                <span className="font-medium">{(subscription.minuteRequestLimit || 0).toLocaleString()} requests</span>
              </div>
            </div>
            {!subscription.canCreateMore && (
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
                <p className="text-sm text-orange-800">
                  You've reached your API key limit for the {subscription.tier || 'current'} plan.
                </p>
              </div>
            )}
          </div>

          {/* Daily Usage Bar */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-green-600" />
              Today's Usage
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Requests Used:</span>
                <span className="font-medium">
                  {(subscription.dailyUsage || 0).toLocaleString()} / {(subscription.dailyRequestLimit || 0).toLocaleString()}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${
                    (subscription.dailyUsagePercentage || 0) >= 90
                      ? 'bg-red-500'
                      : (subscription.dailyUsagePercentage || 0) >= 75
                      ? 'bg-orange-500'
                      : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(100, subscription.dailyUsagePercentage || 0)}%` }}
                ></div>
              </div>

              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>0%</span>
                <span className="font-medium">
                  {(subscription.dailyUsagePercentage || 0).toFixed(1)}% used
                </span>
                <span>100%</span>
              </div>

              {(subscription.dailyUsagePercentage || 0) >= 90 && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800">
                    ⚠️ You're approaching your daily limit. Consider upgrading your plan.
                  </p>
                </div>
              )}

              {(subscription.dailyUsagePercentage || 0) >= 75 && (subscription.dailyUsagePercentage || 0) < 90 && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-800">
                    ⚠️ You've used {(subscription.dailyUsagePercentage || 0).toFixed(1)}% of your daily limit.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Usage Statistics */}
      {usageStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="card p-6">
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-2xl font-bold">{usageStats.totalStats.total_requests || 0}</p>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Successful</p>
                <p className="text-2xl font-bold">{usageStats.totalStats.total_success || 0}</p>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Errors</p>
                <p className="text-2xl font-bold">{usageStats.totalStats.total_errors || 0}</p>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Response</p>
                <p className="text-2xl font-bold">{Math.round(usageStats.totalStats.avg_response_time || 0)}ms</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show newly created key */}
      {showNewKey && (
        <div className="card p-6 mb-6 border-green-200 bg-green-50">
          <div className="flex items-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-green-800">API Key Created Successfully!</h3>
          </div>
          <div className="bg-white p-4 rounded border border-orange-200">
            <div className="flex items-center mb-2">
              <AlertCircle className="w-5 h-5 text-orange-600 mr-2" />
              <p className="text-sm font-medium text-orange-800">
                ⚠️ Save this API key now - it won't be shown again!
              </p>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              For security reasons, only the prefix will be visible after you dismiss this message.
            </p>
            <div className="flex items-center space-x-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all select-all">
                {showNewKey.plainKey}
              </code>
              <button
                onClick={() => copyToClipboard(showNewKey.plainKey)}
                className="btn btn-sm btn-primary px-3 py-2 flex items-center"
                title="Copy full API key"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              💡 Tip: Click the code above to select all, or use the copy button
            </p>
          </div>
          <button
            onClick={() => setShowNewKey(null)}
            className="btn btn-sm btn-soft-success mt-4"
          >
            I've saved my API key, dismiss this message
          </button>
        </div>
      )}

      {/* API Keys Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">
            Your API Keys ({apiKeys.length}/{subscription?.maxApiKeys || 5})
          </h3>
        </div>
        
        {apiKeys.length === 0 ? (
          <div className="text-center py-12">
            <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys</h3>
            <p className="text-gray-600 mb-6">Create your first API key to start accessing the TCG API.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={subscription && !subscription.canCreateMore}
              className={`btn btn-md ${
                subscription && !subscription.canCreateMore
                  ? 'btn-gray cursor-not-allowed opacity-50'
                  : 'btn-primary'
              }`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create API Key
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Limits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiKeys.map((apiKey) => (
                  <tr key={apiKey.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{apiKey.name}</div>
                      <div className="text-sm text-gray-500">Created {formatDate(apiKey.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {apiKey.keyPrefix}
                        </code>
                        <div className="text-xs text-gray-500">
                          (prefix only)
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>Daily: {apiKey.dailyLimit?.toLocaleString() || 'Unlimited'}</div>
                      <div>Per minute: {apiKey.minuteLimit?.toLocaleString() || 'Unlimited'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        apiKey.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {apiKey.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(apiKey.lastUsed)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewLogs(apiKey)}
                          className="btn btn-icon-sm btn-soft-primary"
                          title="View request logs"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingKey(apiKey);
                            setShowEditModal(true);
                          }}
                          className="btn btn-icon-sm btn-soft-info"
                          title="Edit API key"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleKey(apiKey.id)}
                          className={`btn btn-icon-sm ${
                            apiKey.isActive
                              ? 'btn-soft-warning'
                              : 'btn-soft-success'
                          }`}
                          title={apiKey.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {apiKey.isActive ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteKey(apiKey.id)}
                          className="btn btn-icon-sm btn-soft-danger"
                          title="Delete API key"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateApiKeyModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateKey}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingKey && (
        <EditApiKeyModal
          apiKey={editingKey}
          onClose={() => {
            setShowEditModal(false);
            setEditingKey(null);
          }}
          onSubmit={(keyData) => handleUpdateKey(editingKey.id, keyData)}
        />
      )}

      {/* Logs Modal */}
      {showLogsModal && selectedKeyForLogs && (
        <ApiKeyLogsModal
          apiKey={selectedKeyForLogs}
          logs={logs}
          statistics={logsStats}
          pagination={logsPagination}
          loading={logsLoading}
          onClose={() => {
            setShowLogsModal(false);
            setSelectedKeyForLogs(null);
            setLogs([]);
            setLogsStats(null);
            setLogsPagination(null);
          }}
          onPageChange={handleLogsPageChange}
        />
      )}
    </div>
  );
};

// Create API Key Modal Component
const CreateApiKeyModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    dailyLimit: '',
    minuteLimit: '',
    expiresAt: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      name: formData.name,
      ...(formData.dailyLimit && { dailyLimit: parseInt(formData.dailyLimit) }),
      ...(formData.minuteLimit && { minuteLimit: parseInt(formData.minuteLimit) }),
      ...(formData.expiresAt && { expiresAt: formData.expiresAt })
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium mb-4">Create API Key</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="input-field"
              placeholder="My API Key"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Daily Limit (optional)
            </label>
            <input
              type="number"
              value={formData.dailyLimit}
              onChange={(e) => setFormData({...formData, dailyLimit: e.target.value})}
              className="input-field"
              placeholder="10000"
              min="1"
              max="1000000"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Per Minute Limit (optional)
            </label>
            <input
              type="number"
              value={formData.minuteLimit}
              onChange={(e) => setFormData({...formData, minuteLimit: e.target.value})}
              className="input-field"
              placeholder="100"
              min="1"
              max="10000"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiration Date (optional)
            </label>
            <input
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({...formData, expiresAt: e.target.value})}
              className="input-field"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-md btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-md btn-primary">
              Create Key
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit API Key Modal Component
const EditApiKeyModal = ({ apiKey, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: apiKey.name,
    dailyLimit: apiKey.dailyLimit || '',
    minuteLimit: apiKey.minuteLimit || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      name: formData.name,
      ...(formData.dailyLimit && { dailyLimit: parseInt(formData.dailyLimit) }),
      ...(formData.minuteLimit && { minuteLimit: parseInt(formData.minuteLimit) })
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium mb-4">Edit API Key</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="input-field"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Daily Limit
            </label>
            <input
              type="number"
              value={formData.dailyLimit}
              onChange={(e) => setFormData({...formData, dailyLimit: e.target.value})}
              className="input-field"
              placeholder="10000"
              min="1"
              max="1000000"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Per Minute Limit
            </label>
            <input
              type="number"
              value={formData.minuteLimit}
              onChange={(e) => setFormData({...formData, minuteLimit: e.target.value})}
              className="input-field"
              placeholder="100"
              min="1"
              max="10000"
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-md btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-md btn-primary">
              Update Key
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// API Key Logs Modal Component
const ApiKeyLogsModal = ({ apiKey, logs, statistics, pagination, loading, onClose, onPageChange }) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'text-green-600';
    if (statusCode >= 300 && statusCode < 400) return 'text-blue-600';
    if (statusCode >= 400 && statusCode < 500) return 'text-orange-600';
    if (statusCode >= 500) return 'text-red-600';
    return 'text-gray-600';
  };

  const getMethodColor = (method) => {
    const colors = {
      'GET': 'bg-blue-100 text-blue-800',
      'POST': 'bg-green-100 text-green-800',
      'PUT': 'bg-yellow-100 text-yellow-800',
      'DELETE': 'bg-red-100 text-red-800',
      'PATCH': 'bg-purple-100 text-purple-800'
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Request Logs: {apiKey.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                API Key: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{apiKey.keyPrefix}</code>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Statistics */}
        {statistics && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-600">Total Requests</p>
                <p className="text-lg font-semibold">{statistics.total_requests || 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Unique IPs</p>
                <p className="text-lg font-semibold">{statistics.unique_ips || 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Success Rate</p>
                <p className="text-lg font-semibold text-green-600">
                  {statistics.total_requests > 0
                    ? Math.round((statistics.successful_requests / statistics.total_requests) * 100)
                    : 0}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Avg Response Time</p>
                <p className="text-lg font-semibold">{Math.round(statistics.avg_response_time || 0)}ms</p>
              </div>
            </div>
          </div>
        )}

        {/* Logs Table */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No logs found</h3>
              <p className="text-gray-600">This API key hasn't been used yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Response Time</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {formatDateTime(log.created_at)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getMethodColor(log.method)}`}>
                          {log.method}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900 break-all max-w-xs">
                        {log.endpoint}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`text-sm font-semibold ${getStatusColor(log.status_code)}`}>
                          {log.status_code}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 font-mono">
                        {log.ip_address}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {log.response_time_ms ? `${log.response_time_ms}ms` : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total logs)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevious}
                className={`btn btn-sm ${
                  pagination.hasPrevious ? 'btn-secondary' : 'btn-gray cursor-not-allowed opacity-50'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className={`btn btn-sm ${
                  pagination.hasNext ? 'btn-secondary' : 'btn-gray cursor-not-allowed opacity-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="btn btn-md btn-secondary w-full md:w-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiKeys;